import { Fragment } from "react";
import "../../exam/components/question/QuestionCard/QuestionCard.css";
import RenderMathQuestion from "../../../../shared/components/RenderMathQuestion/RenderMathQuestion";
import { optionLables } from "../../../../shared/utilities/constants/optionLabels";
import QuestionText from "../../../analytics/components/QuestionWise/QuestionText";
import images from "../../../../utilities/images/images";
import { getCorrectOptionIds } from "../../../../containers/logged_user_components/evaluation_components/exercise_related_components/exercise_Test_component/exerciseTest_content_component/exercisePaperIdUtils";
import ExerciseQuestionHeader from "./ExerciseQuestionHeader";
import {
  getExerciseAnswerTypeId,
  getExerciseQuestionOptions,
  getExerciseQuestionText,
  isOptionSelected,
} from "../utils/exerciseExamUtils";

const isIntegerAnswerMatch = (typedAnswer, answerKey) => {
  if (!typedAnswer || !answerKey) return false;
  if (typedAnswer === answerKey) return true;
  const typedNumber = Number(typedAnswer);
  const answerNumber = Number(answerKey);
  return (
    Number.isFinite(typedNumber) &&
    Number.isFinite(answerNumber) &&
    typedNumber === answerNumber
  );
};

const ExerciseQuestionCard = ({
  question,
  questionNumber,
  currentLang,
  isPracticeMode = false,
  onToggleLanguage,
  onToggleBookmark,
  onSelectOption,
  onIntegerAnswerChange,
}) => {
  if (!question) return null;

  const answerTypeId = getExerciseAnswerTypeId(question);
  const options = getExerciseQuestionOptions(question, currentLang);
  const questionText = getExerciseQuestionText(question, currentLang);
  const isHindiAvailable = Boolean(question?.q_text_hindi);
  const hasCheckedAnswer = isPracticeMode && question?.answerChecked === true;
  const correctOptionIds = hasCheckedAnswer ? getCorrectOptionIds(question) : [];
  const typedIntegerAnswer = String(question?.integer_answer ?? "").trim();
  const answerKey = String(question?.answer ?? "").trim();
  const isIntegerAnswerCorrect = isIntegerAnswerMatch(
    typedIntegerAnswer,
    answerKey
  );

  const handleIntegerChange = (event) => {
    if (hasCheckedAnswer) return;
    const value = event.target.value;
    // Exercise answers allow a leading minus and up to two decimals.
    if (/^-?\d*\.?\d{0,2}$/.test(value)) {
      onIntegerAnswerChange?.(value);
    }
  };

  return (
    <div className="exam__question_card">
      <ExerciseQuestionHeader
        question={question}
        questionNumber={questionNumber}
        currentLang={currentLang}
        onToggleLanguage={onToggleLanguage}
        onToggleBookmark={onToggleBookmark}
        isHindiAvailable={isHindiAvailable}
        showBookmark={!isPracticeMode}
      />

      <div className="exam__question_card__question">
        <RenderMathQuestion question={questionText || ""} />
      </div>

      <div className="exam__question_card__options">
        {answerTypeId === 3 ? (
          <div className="exam__question_card__integer_input">
            <input
              type="text"
              value={question?.integer_answer ?? ""}
              onChange={handleIntegerChange}
              placeholder="Type your answer here"
              disabled={hasCheckedAnswer}
            />
            {hasCheckedAnswer && answerKey && (
              <div className="exam__question_card__integer_answer_result">
                <div className="exam__question_card__integer_correct_answer_block">
                  <div
                    className={`label your-answer ${
                      isIntegerAnswerCorrect ? "correct" : "wrong"
                    }`}
                  >
                    Your answer: {typedIntegerAnswer || "-"}
                  </div>
                  <div className="label correct-answer">Correct answer is </div>
                  <div className="value">{answerKey}</div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <Fragment>
            {options.map((option, index) => {
              const isSelected = isOptionSelected(question, option.option_id);
              const isCorrectOption = correctOptionIds.some(
                (optionId) => String(optionId) === String(option.option_id)
              );

              let optionClass = "";
              let labelClass = "";
              let statusBadgeText = "";
              let statusBadgeVariant = "";
              let statusBadgeIcon = "";

              if (hasCheckedAnswer) {
                if (isSelected && isCorrectOption) {
                  optionClass = "option-correct";
                  labelClass = "label-correct";
                  statusBadgeText = "Your answer";
                  statusBadgeVariant = "correct";
                  statusBadgeIcon = images.tick_icon;
                } else if (isSelected && !isCorrectOption) {
                  optionClass = "option-wrong";
                  labelClass = "label-wrong";
                  statusBadgeText = "Your answer";
                  statusBadgeVariant = "wrong";
                  statusBadgeIcon = images.Wrong;
                } else if (!isSelected && isCorrectOption) {
                  optionClass = "option-correct";
                  labelClass = "label-correct";
                  statusBadgeText = "Right answer";
                  statusBadgeVariant = "correct";
                  statusBadgeIcon = images.tick_icon;
                }
              } else if (isSelected) {
                optionClass = "selected";
                labelClass = "selected-label";
              }

              return (
                <div
                  key={option.option_id ?? index}
                  className={`exam__question_card__option ${optionClass} ${
                    hasCheckedAnswer ? "option-disabled" : ""
                  }`}
                  onClick={() => {
                    if (hasCheckedAnswer) return;
                    onSelectOption?.(option.option_id);
                  }}
                >
                  <span
                    className={`exam__question_card__option_label ${
                      labelClass || "not-selected-label"
                    }`}
                  >
                    {optionLables[index]}
                  </span>

                  <RenderMathQuestion
                    question={String(option.option ?? "")}
                  />
                  {statusBadgeText && (
                    <span
                      className={`exam__question_card__option_status ${statusBadgeVariant}`}
                    >
                      <img src={statusBadgeIcon} alt="" />
                      {statusBadgeText}
                    </span>
                  )}
                </div>
              );
            })}
          </Fragment>
        )}
      </div>
      {hasCheckedAnswer && question?.showSolution === true && (
        <QuestionText
          data={{
            ...question,
            sol_text: question.sol_text || question.solution || "",
            sol_text_hindi:
              question.sol_text_hindi || question.solution_hindi || "",
          }}
          solutionOnly
          language={currentLang}
        />
      )}
    </div>
  );
};

export default ExerciseQuestionCard;
